import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// ui-connect
import { Spinner, Alert } from 'ui-connect';

// general components
import Translate from 'components/Translate';

// Redux
import { fetchSystemPreference, showModal } from 'actions';
import {
    fetchBadgeDetails,
    updateViewedBadges,
    receiveEarnedBadges,
    receiveLatestEarnedBadges
} from 'actions/badgesActions';
import { getBadges } from 'reducers/badge/badges';
import { fetchMultipleUserSocialProfileImage } from 'actions/userSocialProfileImageActions';
import { selectProfileImageDtoMapState } from 'reducers/userSocialProfileImage/userSocialProfileImageReducer';

// constannts
import { VIEW } from '../../Badges/constants/badgesPageConstants';

/** feature flags */
import { getSystemPreferenceValue } from 'reducers/system/systemPreferences';
import {
    gcPlusBadgePointRoleKey // Clean up ticket: CA-172570
} from 'models/preferences/PreferencesMapping';

// GC+
import { useUserHasPremiumSocialImprovements } from 'services/omt/subscription/customerPremiumFeature';

// local components
import BadgeImage from './components/BadgeImage';
import BadgeProgress from './components/BadgeProgress';
import BadgeEarnedMessage from './components/BadgeEarnedMessage';
import BadgeSummary from './components/BadgeSummary';
import RelatedBadges from './components/RelatedBadges';
import ConnectionsWithBadge from './components/ConnectionsWithBadge';
import ViewerEarnedStatus from './components/ViewerEarnedStatus';
import BadgeAvailableDate from './components/BadgeAvailableDate';

const mapStateToProps = (state) => ({
    badges: getBadges(state),
    profileImageDtoMapState: selectProfileImageDtoMapState(state),
    gcPlusBadgePointRoleValue: getSystemPreferenceValue(state, gcPlusBadgePointRoleKey)
});

const mapDispatchToProps = {
    fetchSystemPreference,
    updateViewedBadges,
    receiveEarnedBadges,
    receiveLatestEarnedBadges,
    showModal,
    fetchMultipleUserSocialProfileImage
};

class BadgeDetails extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            view: null,
            badge: null,
            error: false
        };

        this.displayName = null;
    }

    componentDidMount() {
        this.getBadgeData();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.badge.badgeId !== this.props.badge.badgeId) {
            this.getBadgeData();
        }
    }

    getBadgeData() {
        const { badge, viewerSocialProfile, displayName } = this.props;
        let view = null,
            connectionDisplayName = null;

        if (this.state.badge !== null) {
            this.setState({
                badge: null
            });
        }

        // The displayName is what we use to figure out which details view to load (Earned on Connection)
        // If the received displayName is equal to the viewer's displayName, we show the Earned/Available view, otherwise, we show the Connection view
        this.displayName = displayName !== undefined ? displayName : badge.displayName;

        if (this.displayName !== null && this.displayName !== viewerSocialProfile.displayName) {
            // Viewing a connection's badge
            view = VIEW.CONNECTION;
            connectionDisplayName = this.displayName;
        } else {
            // Viewing either an earned or an available badge
            if (this.displayName === null) {
                view = VIEW.AVAILABLE;
            } else {
                view = VIEW.EARNED;
            }

            this.markNewBadgeAsSeen();
        }

        this.setState({
            view
        });

        // get badge details
        fetchBadgeDetails(badge.badgeId, connectionDisplayName)
            .then((response) => {
                this.setState({
                    badge: response
                });
                if (Array.isArray(response?.connections) && response?.connections?.length > 0) {
                    const userIds = response.connections.map((item) => item.userProfileId);
                    this.props.fetchMultipleUserSocialProfileImage(userIds);
                }
            })
            .catch(() => {
                this.setState({
                    error: true
                });
            });
    }

    render() {
        const { badge, view, error } = this.state;

        if (error) {
            return (
                <Alert type="error">
                    <Translate ns="notification" content="request.error.msg" />
                </Alert>
            );
        }

        if (badge) {
            switch (view) {
                case VIEW.EARNED:
                case VIEW.AVAILABLE:
                    return this.renderUserView();
                case VIEW.CONNECTION:
                    return this.renderConnectionView();
                default:
                    return null;
            }
        } else {
            return (
                <div className="textCenter marBottomXS">
                    <Spinner />
                </div>
            );
        }
    }

    renderUserView() {
        const { badge } = this.state;

        // NOTE - Always pass badge={badge} as the last param (after {...this.props}) to any sub-component such that the full BadgeDTO is used.
        // Otherwise, the incomplete BadgeDTO that comes from the badges list will be used
        return (
            <React.Fragment>
                <div className={`badge-detail ${!badge.earnedByMe ? 'badge-available' : 'badge-earned'}`}>
                    <BadgeImage {...this.props} badge={badge} />
                    {badge.badgeProgressValue ? (
                        <BadgeProgress
                            {...this.props}
                            showValues={true}
                            badgeProgressValue={badge.badgeProgressValue}
                            badgeTargetValue={badge.badgeTargetValue}
                            badgeUnitId={badge.badgeUnitId}
                        />
                    ) : null}
                    <div className="badge-summary">
                        <BadgeSummary
                            {...this.props}
                            badge={badge}
                            earned={badge.earnedByMe}
                            showMultiplier={this.shouldShowMultiplier()}
                        />
                    </div>
                    <BadgeEarnedMessage {...this.props} badge={badge} displayName={this.displayName} />
                </div>
                <RelatedBadges {...this.props} badge={badge} />
                <ConnectionsWithBadge {...this.props} badge={badge} />
                <BadgeAvailableDate {...this.props} badge={badge} />
            </React.Fragment>
        );
    }

    renderConnectionView() {
        const { badge } = this.state;
        const { noStatusLinkOnConnectionView, viewerSocialProfile } = this.props;

        // NOTE - Always pass badge={badge} as the last param (after {...this.props}) to any sub-component such that the full BadgeDTO is used.
        // Otherwise, the incomplete BadgeDTO that comes from the badges list will be used
        return (
            <div className={`badge-detail badge-earned`}>
                <BadgeImage {...this.props} badge={badge} />
                <div className="badge-summary">
                    <BadgeSummary {...this.props} badge={badge} />
                </div>
                <BadgeEarnedMessage {...this.props} badge={badge} displayName={this.displayName} isConnection={true} />

                {viewerSocialProfile.displayName ? ( // if the viewer is logged in
                    <ViewerEarnedStatus
                        {...this.props}
                        badge={badge}
                        connection={!noStatusLinkOnConnectionView ? this.displayName : null}
                    />
                ) : null // otherwise we don't show the "You've received this badge" message since we don't know who the viewer is
                }
            </div>
        );
    }

    markNewBadgeAsSeen() {
        const { badge, badges } = this.props;

        if (badge && badge.badgeIsViewed === false && badge.earnedByMe) {
            this.props.updateViewedBadges([badge.badgeId]);

            // update local copy
            const earnedBadgesCopy = badges.earned.data ? [...badges.earned.data] : [];
            const latestEarnedBadgesCopy = badges.latestEarned.data ? [...badges.latestEarned.data] : [];

            const earnedBadge = earnedBadgesCopy.find((b) => {
                return b.badgeId === badge.badgeId;
            });

            const latestEarnedBadge = latestEarnedBadgesCopy.find((b) => {
                return b.badgeId === badge.badgeId;
            });

            if (earnedBadge) {
                earnedBadge.badgeIsViewed = true;
                this.props.receiveEarnedBadges(earnedBadgesCopy);
            }

            if (latestEarnedBadge) {
                latestEarnedBadge.badgeIsViewed = true;
                this.props.receiveLatestEarnedBadges(latestEarnedBadgesCopy);
            }
        }
    }

    shouldShowMultiplier = () => {
        const {
            badge,
            viewerSocialProfile,
            gcPlusBadgePointRoleValue,
            displayName: propsDisplayName,
            hasPremiumFeature
        } = this.props;
        if (!viewerSocialProfile?.userRoles?.includes(gcPlusBadgePointRoleValue)) {
            return false;
        }
        // The displayName is what we use to figure out which details view to load (Earned on Connection)
        // If the received displayName is equal to the viewer's displayName, we show the Earned/Available view, otherwise, we show the Connection view
        const displayName = propsDisplayName !== undefined ? propsDisplayName : badge.displayName;

        /**
         * badge is viewer's
         */
        if (displayName !== null) {
            return viewerSocialProfile?.displayName === displayName;
        }

        return hasPremiumFeature;
    };
}

BadgeDetails.propTypes = {
    badge: PropTypes.object.isRequired,
    viewerSocialProfile: PropTypes.object.isRequired
};

/**
 * `BadgeDetails` wrapped by tanstack query
 * @param {Object} props
 * @param {Object} [props.viewerSocialProfile]
 * @param {string} props.viewerSocialProfile.garminGUID
 */
const BadgeDetailsWrappedQuery = (props) => {
    const { viewerSocialProfile } = props;

    const { isAvailable: hasPremiumFeature } = useUserHasPremiumSocialImprovements(
        viewerSocialProfile?.garminGUID || '',
        {
            enabled: !!viewerSocialProfile?.garminGUID
        }
    );

    return <BadgeDetails {...props} hasPremiumFeature={hasPremiumFeature} />;
};

export default connect(mapStateToProps, mapDispatchToProps)(BadgeDetailsWrappedQuery);
